import React, {useEffect, useState} from 'react';
import {TextInput, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {S, STATUS, T} from '../courseTheme';
import {formatClock, parseClock, trimProblem} from './editorLogic';
import {E} from './editorTheme';
import {Button} from './ui';

type Props = {
  /** The chapter's saved times, on its recording's clock. */
  start: number;
  end: number;
  playhead: number | null;
  saving: boolean;
  /** Put the playhead here, to hear what a cut is about to include. */
  onPreview: (seconds: number) => void;
  /** The times as typed, for the strip above to follow; null while unreadable. */
  onDraft: (start: number | null, end: number | null) => void;
  onSave: (start: number, end: number) => void;
  onCancel: () => void;
};

/** How much of the end "Play to the end" plays. */
const END_PREVIEW_SEC = 5;

/**
 * The chapter's two cuts, set by hand: typed (12:34 or 12:34.5), nudged a
 * second at a time, or taken from the playhead — the video plays past both
 * ends meanwhile, so a cut can be heard before it is moved. Saving sends the
 * same `set_times` op the chat does; the server moves the neighbouring
 * chapter's matching cut with it and refuses anything it would not play.
 */
export default function TrimPanel({
  start,
  end,
  playhead,
  saving,
  onPreview,
  onDraft,
  onSave,
  onCancel,
}: Props) {
  const [startText, setStartText] = useState(formatClock(start));
  const [endText, setEndText] = useState(formatClock(end));
  const from = parseClock(startText);
  const to = parseClock(endText);
  const problem = trimProblem(from, to);
  const unchanged =
    from != null && to != null && Math.abs(from - start) < 0.05 && Math.abs(to - end) < 0.05;

  useEffect(() => {
    onDraft(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const nudge = (which: 'start' | 'end', by: number) => {
    const value = which === 'start' ? from : to;
    if (value == null) return;
    const next = formatClock(Math.max(0, Math.round((value + by) * 10) / 10));
    (which === 'start' ? setStartText : setEndText)(next);
  };

  const row = (which: 'start' | 'end') => {
    const isStart = which === 'start';
    const word = isStart ? 'Start' : 'End';
    const value = isStart ? from : to;
    const text = isStart ? startText : endText;
    const setText = isStart ? setStartText : setEndText;
    const unreadable = value == null;
    return (
      <View style={{flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: S[2]}}>
        <CCText style={{width: 40, fontSize: T.sm + 1, fontWeight: '600', color: E.ink}}>
          {word}
        </CCText>
        <TextInput
          value={text}
          onChangeText={setText}
          accessibilityLabel={`${word} time`}
          placeholder="12:34"
          placeholderTextColor={E.muted}
          inputMode="decimal"
          style={{
            width: 96,
            minHeight: 44,
            borderWidth: 1,
            borderColor: unreadable ? STATUS.bad.fg : E.inputLine,
            borderRadius: 10,
            paddingHorizontal: S[3],
            fontSize: T.base,
            color: E.ink,
            fontVariant: ['tabular-nums'],
          }}
        />
        <Button
          label="−1 s"
          variant="secondary"
          accessibilityLabel={`${word} one second earlier`}
          disabled={unreadable}
          onPress={() => nudge(which, -1)}
        />
        <Button
          label="+1 s"
          variant="secondary"
          accessibilityLabel={`${word} one second later`}
          disabled={unreadable}
          onPress={() => nudge(which, 1)}
        />
        <Button
          label="Use playhead"
          variant="quiet"
          icon="locate-outline"
          accessibilityLabel={`Set the ${which} to the playhead`}
          disabled={playhead == null}
          onPress={() => playhead != null && setText(formatClock(playhead))}
        />
        <Button
          label={isStart ? 'Play from start' : 'Play to the end'}
          variant="quiet"
          icon="play-outline"
          disabled={unreadable}
          onPress={() =>
            value != null &&
            onPreview(isStart ? value : Math.max(from ?? 0, value - END_PREVIEW_SEC))
          }
        />
      </View>
    );
  };

  return (
    <View
      accessibilityLabel="Chapter times"
      style={{
        gap: S[2],
        padding: S[3],
        borderWidth: 1,
        borderColor: E.line,
        borderRadius: 12,
        backgroundColor: E.surface,
      }}>
      {row('start')}
      {row('end')}
      <View style={{flexDirection: 'row', alignItems: 'flex-start', gap: 6}}>
        <Ionicons name="information-circle-outline" size={16} color={E.muted} />
        <CCText style={{fontSize: T.sm, color: E.muted, flexShrink: 1, lineHeight: 18}}>
          The video plays past both cuts while you adjust. A neighbouring chapter that shares a
          cut moves with it.
        </CCText>
      </View>
      {problem ? (
        <CCText
          accessibilityRole="alert"
          style={{fontSize: T.sm, color: STATUS.bad.fg, lineHeight: 18}}>
          {problem}
        </CCText>
      ) : null}
      <View style={{flexDirection: 'row', gap: S[2], flexWrap: 'wrap'}}>
        <Button
          label={saving ? 'Saving…' : 'Save times'}
          variant="accent"
          icon="checkmark"
          disabled={!!problem || unchanged || saving}
          onPress={() => from != null && to != null && onSave(from, to)}
        />
        <Button label="Cancel" variant="quiet" onPress={onCancel} />
      </View>
    </View>
  );
}
